/**
 * Short tones, so the athlete does not have to look at the screen.
 *
 * This matters more than it sounds. The drill is filmed side-on, which means the
 * athlete is standing at ninety degrees to the phone: reading a verdict off the
 * screen costs them a turn of the head on every rep, which is a change to the
 * movement being measured. A sound they can tell apart without looking is the
 * only feedback that does not interfere with the thing it is reporting on.
 *
 * Everything here is guarded and silent on failure. Audio is blocked outright on
 * some devices and in some settings, and a missing tone must never take the drill
 * down with it — the on-screen verdict is always there as well.
 */

/** One shared context. iOS will refuse to start more than a few. */
let context = null;

function acquire() {
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) {
    return null;
  }
  context = context || new Ctor();
  if (context.state === "suspended") {
    context.resume();
  }
  return context;
}

/**
 * Creates and resumes the audio context. Must be called from inside a click or
 * key handler.
 *
 * Browsers only allow audio to begin from a user gesture, and on iOS a context
 * created outside one stays suspended for good. The rep tones fire on their own
 * long after any gesture, so the Start button is what unlocks them: without this
 * the whole point of the feature would be silently lost on exactly the device the
 * drill is filmed on.
 */
export function unlockAudio() {
  try {
    acquire();
  } catch {
    // Silence is acceptable; a thrown error during setup is not.
  }
}

/**
 * The lowest pitch a phone speaker actually reproduces.
 *
 * Measured the hard way: the first version of the fault tone fell to 190Hz, and
 * on a phone propped up across the room it was inaudible. A phone speaker is a
 * few millimetres across and rolls off steeply below roughly 500Hz, so a low
 * tone is not a quiet warning — it is no warning at all. Nothing here goes below
 * this, which costs the fault sound the obvious "low means bad" and is why it
 * carries its difference in timbre and length instead.
 */
const SPEAKER_FLOOR = 440;

/**
 * @param frequency Hz. Must not go below SPEAKER_FLOOR.
 * @param seconds How long the tone lasts.
 * @param after Seconds to wait before it starts, on the audio clock rather than
 *   a timer, so a two-note figure stays tight whatever the main thread is doing.
 * @param type Oscillator shape, and the app's three voices are the three a
 *   console had: "square" is buzzy and cuts through a room, which is what the
 *   fault sound needs; "triangle" is the softer chiptune voice, for the good rep;
 *   "sine" is the clean fallback. A sine is the one shape no 8-bit chip could
 *   produce, which is why nothing here uses it by default any more.
 */
export function tone(frequency, seconds, after = 0, type = "square") {
  try {
    const ctx = acquire();
    if (ctx == null) {
      return;
    }
    const start = ctx.currentTime + after;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = type;
    oscillator.frequency.value = Math.max(frequency, SPEAKER_FLOOR);
    // A square wave carries far more energy than a sine at the same amplitude,
    // so it is turned down to sit at a comparable loudness rather than a harsh one.
    gain.gain.setValueAtTime(type === "square" ? 0.08 : 0.12, start);
    // Fade out, because an abruptly stopped oscillator clicks.
    gain.gain.exponentialRampToValueAtTime(0.0001, start + seconds);
    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(start + seconds);
  } catch {
    // No audio available; the on-screen readout still carries it.
  }
}

/**
 * A good rep: two soft notes rising, and over quickly.
 *
 * Short on purpose. A good rep needs only to be confirmed, and the athlete is
 * already into the next one. The triangle voice is the gentler of the two, which
 * is the right way round: the sound you want to hear nine times out of ten should
 * not be the abrasive one.
 */
export function repGood() {
  tone(880, 0.09, 0, "triangle");
  tone(1320, 0.12, 0.1, "triangle");
}

/**
 * A faulted rep: three buzzy notes descending, twice as long as a good one.
 *
 * It differs from the good rep in three ways at once — timbre, direction and
 * length — because any one of them can be lost. Pitch alone cannot carry it: the
 * obvious choice of a low tone for a fault is the one thing a phone speaker
 * cannot reproduce (see SPEAKER_FLOOR), so the whole figure stays above 440 and
 * leans on the square wave against the good rep's triangle, and on the longer
 * pattern, to read as a flag. It also stays clear of the countdown's 660 and 1040
 * so a fault is never heard as the set starting.
 */
export function repFault() {
  tone(740, 0.13, 0, "square");
  tone(590, 0.13, 0.14, "square");
  tone(440, 0.22, 0.28, "square");
}

/**
 * Plays both verdict sounds, good then fault, so the athlete can check they can
 * hear the difference before a set rather than during one.
 *
 * This exists because the first version of the fault sound could not be heard on
 * a phone at all, and there was no way to find that out without performing a bad
 * rep on purpose.
 */
export function soundCheck() {
  repGood();
  // After the good figure has finished, with a clear gap: they are being
  // compared, not blended.
  setTimeout(repFault, 700);
}
